import { ActionContext, ActionTree } from 'vuex'
import State from '@/store/state'
import { MutationTypes } from '@/store/mutations'
import { client } from '@/lib/api'
import { preventError } from '@/utils'
import { gql } from 'apollo-boost'
import {
  UpdateSurveyPositionInput,
  CreateCategoryInput,
  CreateQuestionInput,
  CreateStudyInput,
  GetCategoriesInput,
  GetCurrentUserInput,
  GetInstitutionsInput,
  GetProceduresInput,
  GetStudiesInput,
  GetSurveysInput,
  GetSurveyChartDataInput,
  UpdateProcedureInput,
  UpdateQuestionPositionInput,
  UpdateQuestionInput,
  CreateInstitutionInput,
  UpdateInstitutionInput,
  UpdateStudyInput,
  UpdateSurveyInput,
  DeleteStudyInput,
  GetLearningObjectsInput,
  CreateLearningObjectInput,
  UpdateLearningObjectInput,
  DeleteLearningObjectInput,
  DeleteLearningObjectResponse,
  GetSurveyResultInput,
  GetSurveyChartDataResponse,
  UpdateCategoryInput,
  DeleteCategoryInput,
  DeleteQuestionCategoryInput,
  GetMostPopularProceduresInput,
  GetInfrequentProceduresInput,
  GetNursesExperienceInput,
  GetAveragePatientAcuityDataInput,
  GetNurseAgeExperienceStatsInput,
  GetNurseAgeStatsInput,
  GetAverageShiftDataInput,
  GetDevicesInput,
  GetGroupsInput,
  CreateDeviceInput,
  GetDeviceInput,
  UpdateDeviceInput,
  GetGroupInput,
  GetDeviceReportTotalInput,
  GetDeviceReportInput,
  CreateGroupInput,
  GetDeviceChartsInput,
  GetRateOfFailedAuditsInput,
  GetNumberOfExecutedProceduresInput,
  GetAverageTimeSpentOnProcedureInput,
  GetAverageNurseAgeChartInput,
  GetNursesGenderChartInput,
  GetFiveLeastFrequentlyProceduresInput,
  GetFiveMostAssistedProceduresInput,
  GetFiveMostFrequentlyProceduresInput,
  GetShiftTirednessChartInput,
  GetShiftIntensityChartInput,
  GetShiftTiredenessQuestionsInput,
  GetShiftIntensityQuestionsInput,
  GetPatientsInput,
  AddPatientInput,
  AddPatientVisitInput,
  CreateProcedureExecutionInput
} from '@/generated/graphql'

import { stepFragment } from '@/store/fragments'
import { useToast } from 'vue-toastification'

const toast = useToast()

export enum ActionTypes {
  CREATE_INSTITUTION = 'CREATE_INSTITUTION',
  GET_PROCEDURES = 'GET_PROCEDURES',
  GET_PROCEDURE = 'GET_PROCEDURE',
  GET_CATEGORIES = 'GET_CATEGORIES',
  DELETE_PROCEDURE = 'DELETE_PROCEDURE',
  CREATE_PROCEDURE = 'CREATE_PROCEDURE',
  GET_CURRENT_USER = 'GET_CURRENT_USER',
  UPDATE_PROCEDURE = 'UPDATE_PROCEDURE',
  DELETE_STEP = 'DELETE_STEP',
  ADD_PROCEDURE_STEP = 'ADD_PROCEDURE_STEP',
  UPDATE_PROCEDURE_STEP = 'UPDATE_PROCEDURE_STEP',
  GET_INSTITUTIONS = 'GET_INSTITUTIONS',
  GET_STUDIES = 'GET_STUDIES',
  CREATE_STUDY = 'CREATE_STUDY',
  UPDATE_STUDY = 'UPDATE_STUDY',
  DELETE_STUDY = 'DELETE_STUDY',
  GET_SURVEYS = 'GET_SURVEYS',
  CREATE_SURVEY = 'CREATE_SURVEY',
  UPDATE_SURVEY = 'UPDATE_SURVEY',
  GET_SURVEY_CHART_DATA = 'GET_SURVEY_CHART_DATA',
  CREATE_QUESTION = 'CREATE_QUESTION',

  UPDATE_QUESTION = 'UPDATE_QUESTION',
  CREATE_CATEGORY = 'CREATE_CATEGORY',
  UPDATE_CATEGORY = 'UPDATE_CATEGORY',
  DELETE_CATEGORY = 'DELETE_CATEGORY',
  DELETE_QUESTION_CATEGORY = 'DELETE_QUESTION_CATEGORY',
  REPOSITION_SURVEY = 'REPOSITION_SURVEY',
  REPOSITION_QUESTION = 'REPOSITION_QUESTION',
  UPDATE_INSTITUTION = 'UPDATE_INSTITUTION',
  GET_LEARNING_OBJECTS = 'GET_LEARNING_OBJECTS',
  CREATE_LEARNING_OBJECT = 'CREATE_LEARNING_OBJECT',
  UPDATE_LEARNING_OBJECT = 'UPDATE_LEARNING_OBJECT',
  DELETE_LEARNING_OBJECT = 'DELETE_LEARNING_OBJECT',
  GET_SURVEY_RESULT_ANSWERS = 'GET_SURVEY_RESULT_ANSWERS',
  GET_MOST_POPULAR_PROCEDURES = 'GET_MOST_POPULAR_PROCEDURES',
  GET_INFREQUENT_PROCEDURES = 'GET_INFREQUENT_PROCEDURES',
  GET_NURSES_EXPERIENCE_STATS = 'GET_NURSES_EXPERIENCE_STATS',
  GET_NURSE_AGE_EXPERIENCE_STATS = 'GET_NURSE_AGE_EXPERIENCE_STATS',
  GET_NURSE_AGE_STATS = 'GET_NURSE_AGE_STATS',
  GET_NURSE_SEX_STATS = 'GET_NURSE_SEX_STATS',
  GET_AVERAGE_SHIFT_DATA = 'GET_AVERAGE_SHIFT_DATA',
  GET_AVERAGE_ACUITY_DATA = 'GET_AVERAGE_ACUITY_DATA',
  GET_DEVICE = 'GET_DEVICE',
  GET_DEVICES = 'GET_DEVICES',
  CREATE_DEVICE = 'CREATE_DEVICE',
  CREATE_DEVICES = 'CREATE_DEVICES',
  UPDATE_DEVICE = 'UPDATE_DEVICE',
  GET_GROUPS = 'GET_GROUPS',
  GET_GROUP = 'GET_GROUP',
  CREATE_GROUP = 'CREATE_GROUP',
  UPDATE_GROUP = 'UPDATE_GROUP',
  DELETE_GROUP = 'DELETE_GROUP',
  GET_DEVICE_REPORTS_TOTAL = 'GET_DEVICE_REPORTS_TOTAL',
  GET_DEVICE_REPORT = 'GET_DEVICE_REPORT',
  GET_DEVICE_CHARTS = 'GET_DEVICE_CHARTS',
  GET_RATE_OF_FAILED_AUDITS = 'GET_RATE_OF_FAILED_AUDITS',
  GET_NUMBER_OF_EXECUTED_PROCEDURES_CHART_DATA = 'GET_NUMBER_OF_EXECUTED_PROCEDURES_CHART_DATA',
  GET_AVERAGE_PROCEDURE_TIME_CHART_DATA = 'GET_AVERAGE_PROCEDURE_TIME_CHART_DATA',
  GET_AVERAGE_NURSE_AGE_CHART = 'GET_AVERAGE_NURSE_AGE_CHART',
  GET_NURSES_GENDER_CHART = 'GET_NURSES_GENDER_CHART',
  GET_FIVE_MOST_FREQUENTLY_PROCEDURES = 'GET_FIVE_MOST_FREQUENTLY_PROCEDURES',
  GET_FIVE_LEAST_FREQUENTLY_PROCEDURES = 'GET_FIVE_LEAST_FREQUENTLY_PROCEDURES',
  GET_SURVEY_RESULTS = 'GET_SURVEY_RESULTS',
  GET_FIVE_MOST_ASSISTED_PROCEDURES = 'GET_FIVE_MOST_ASSISTED_PROCEDURES',
  GET_SHIFT_TIREDNESS_CHART = 'GET_SHIFT_TIREDNESS_CHART',
  GET_SHIFT_INTENSITY_CHART = 'GET_SHIFT_INTENSITY_CHART',
  GET_SHIFT_TIREDNESS_QUESTIONS = 'GET_SHIFT_TIREDNESS_QUESTIONS',
  GET_SHIFT_INTENSITY_QUESTIONS = 'GET_SHIFT_INTENSITY_QUESTIONS',
  SET_PAGINATION_TABLE_SHOW_SIZE = 'SET_PAGINATION_TABLE_SHOW_SIZE',
  GET_PATIENTS = 'GET_PATIENTS',
  ADD_PATIENT = 'ADD_PATIENT',
  ADD_PATIENT_PROCEDURE = 'ADD_PATIENT_PROCEDURE',
  ADD_PATIENT_VISIT = 'ADD_PATIENT_VISIT',
  GET_PATIENT = 'GET_PATIENT',
  UPDATE_PATIENT = 'UPDATE_PATIENT',
  UPDATE_PATIENT_PROCEDURE = 'UPDATE_PATIENT_PROCEDURE',
  GET_PATIENT_CONSENT = 'GET_PATIENT_CONSENT',
  UPDATE_PATIENT_VISIT = 'UPDATE_PATIENT_VISIT',
  DELETE_PATIENT_VISIT = 'DELETE_PATIENT_VISIT',
  DELETE_PATIENT_PROCEDURE = 'DELETE_PATIENT_PROCEDURE',
  GET_SHIFTS_WITH_VISITS = 'GET_SHIFTS_WITH_VISITS',
  GET_VISIT_CHECKLIST_BY_VISIT_ID = 'GET_VISIT_CHECKLIST_BY_VISIT_ID',
  GET_SHIFTS = 'GET_SHIFTS',
  GET_NURSES_INFO_COMBINED = 'GET_NURSES_INFO_COMBINED',
  GET_VISITS_BY_TIME_PERIOD = 'GET_VISITS_BY_TIME_PERIOD',
}

// TODO: Store queries somewhere central place
// TODO: Implement query fragments

const actions: ActionTree<State, any> = {
  async [ActionTypes.GET_PROCEDURE](store: ActionContext<State, any>, payload) {
    const input: GetProceduresInput = {
      institutionId: payload?.institutionId
        ? [payload.institutionId]
        : [store.getters.activeInstitutionId]
    }

    if (payload.procedureId) {
      input.id = [payload.procedureId]
    }

    if (payload.procedureCode) {
      input.code = [payload.procedureCode]
    }
    try {
      const response = await client.query({
        query: gql`
          query getProcedures($input: GetProceduresInput!) {
            getProcedures(input: $input) {
              items {
                name
                id
                code
                isPublic
                isEnabled
                description
                language
                steps {
                  ...ProcedureStepDetails
                }
                departments {
                  id
                  name
                  institution {
                    name
                  }
                }
                checklist
                createdAt
                updateAt
                category {
                  id
                  name
                }
                author {
                  id
                  name
                }
                departments {
                  name
                  institution {
                    name
                  }
                }
              }
            }
          }
          ${stepFragment.step}
        `,
        variables: { input }
      })
      store.state.refreshProcedures = false
      store.commit(
        MutationTypes.SET_ACTIVE_PROCEDURE,
        response.data.getProcedures.items[0]
      )
      return store.getters.activeProcedure
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.GET_PROCEDURES](
    store: ActionContext<State, any>,
    payload
  ) {
    const { pager, search, freezeCache } = payload
    const input: GetProceduresInput = {
      institutionId:
        payload?.institutionId || store.getters.activeInstitutionId,
      departmentId: payload.departmentId,
      pager,
      search
    }

    try {
      const response = await client.query({
        query: gql`
          query getProcedures($input: GetProceduresInput!) {
            getProcedures(input: $input) {
              items {
                id
                code
                name
                isPublic
                isEnabled
                createdAt
                departments {
                  name
                }
                category {
                  name
                }
              }
              pager {
                count
                offset
                limit
              }
            }
          }
        `,
        variables: { input }
      })
      if (!freezeCache) {
        store.commit(
          MutationTypes.SET_PROCEDURE_LIST,
          response.data.getProcedures.items
        )
        store.commit(
          MutationTypes.SET_PROCEDURE_PAGEINFO,
          response.data.getProcedures.pager
        )
        store.state.refreshProcedures = false
      }
      return response.data.getProcedures
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.GET_CURRENT_USER](
    store: ActionContext<State, any>,
    payload
  ) {
    const input: GetCurrentUserInput = {}
    let contextObj = {}

    if (payload && payload.token) {
      input.signUpToken = payload.token
      contextObj = {
        context: {
          headers: {
            Authorization: `Bearer ${payload.token}`
            // accept: 'application/json',
            // 'Content-Type': 'application/json'
          }
        }
      }
    }

    try {
      const response = await client.query({
        query: gql`
          query currentUser($input: GetCurrentUserInput) {
            currentUser(input: $input) {
              profile {
                id
                name
                email
                language
                avatarUrl
              }
              roles {
                departmentId
                departmentName
                institutionId
                institutionName
                role
                roleName
                priority
              }
              activeSub
            }
          }
        `,
        variables: { input },
        errorPolicy: 'all'
        // ...contextObj
      })

      if (response.errors?.length > 0) {
        throw new Error(response.errors[0].message)
      }

      store.commit(
        MutationTypes.SET_CURRENT_USER,
        response.data.currentUser.profile
      )

      store.commit(
        MutationTypes.SET_CURRENT_ROLES,
        response.data.currentUser.roles
      )

      store.commit(
        "SET_USER_SUB_IS_ACTIVE",
        response.data.currentUser.activeSub
      )

      return {
        profile: response.data.currentUser.profile,
        roles: response.data.currentUser.roles
      }
    } catch (e: any) {
      const result = e?.networkError?.result
      const code = result?.error || 'UNKNOWN_ERROR'

      if (code === 'USER_NOT_FOUND' && window.localStorage.getItem('apollo-token')) {
        window.localStorage.removeItem('apollo-token')
        // let App.vue handle redirect and logout
        throw code
      }

      throw code
    }

  },

  async [ActionTypes.GET_CATEGORIES](
    store: ActionContext<State, any>,
    payload
  ) {
    try {
      const input: GetCategoriesInput = {
        institutionId: store.getters.activeInstitutionId,
        categoryType: payload.categoryType
      }

      const response = await client.query({
        query: gql`
          query getCategories($input: GetCategoriesInput!) {
            getCategories(input: $input) {
              items {
                id
                name
              }
            }
          }
        `,
        variables: { input }
      })
      if (!payload?.freezeCache) {
        store.commit(
          MutationTypes.SET_CATEGORY_LIST,
          response.data.getCategories.items
        )
      }
      return response.data.getCategories
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.DELETE_PROCEDURE](
    store: ActionContext<State, any>,
    payload: any
  ) {
    const input = {
      id: payload.procedureId
    }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation deleteProcedure($input: DeleteProcedureInput!) {
            deleteProcedure(input: $input) {
              status
              message
              code
            }
          }
        `,
        variables: { input }
      })
      if (response.data.deleteProcedure.status) {
        await store.commit(MutationTypes.SET_PROCEDURE_REFRESH, true)
        toast.info('Procedure deleted')
      } else {
        toast.warning('Procedure not deleted')
      }
    } catch (e) {
      preventError(e);
    }
  },

  async [ActionTypes.DELETE_STEP](
    store: ActionContext<State, any>,
    payload: any
  ) {
    const activeProcedure = store.state.activeProcedure
    if (activeProcedure.id !== payload.procedureId) {
      throw new Error('Procedure id mismatch')
    }

    const procedureData: UpdateProcedureInput = {
      id: payload.procedureId,
      steps: [].concat(payload.steps)
    }
    const result = await store.dispatch(
      ActionTypes.UPDATE_PROCEDURE,
      procedureData
    )
    if (result.status) {
      toast.info(`Deleted step from procedure ${result.procedure.name}`)
    }
    return result
  },

  async [ActionTypes.CREATE_PROCEDURE](
    store: ActionContext<State, any>,
    payload
  ) {
    payload.departmentId =
      payload.departmentId || store.getters.activeDepartmentId
    payload.institutionId = store.getters.activeInstitutionId
    payload.categoryId = payload.category
    payload.isPublic = payload.public === 'public'
    delete payload.category
    delete payload.public
    if (payload.steps && payload.steps.length) {
      payload.steps = payload.steps.map((stepObject) => {
        // remove and restructure keys to match with API needs
        const { order, errors, displayName, title, ...rest } = stepObject
        return Object.assign({}, rest, { title: title.name })
      })
    }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createProcedure($input: CreateProcedureInput!) {
            createProcedure(input: $input) {
              status
              procedure {
                code
              }
            }
          }
        `,
        variables: { input: payload }
      })
      if (response.data?.createProcedure?.status) {
        toast.info('Procedure created')
        return response.data.createProcedure
      }
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.UPDATE_PROCEDURE](
    store: ActionContext<State, any>,
    payload
  ) {
    payload.categoryId = payload.category
    if (payload.public) {
      payload.isPublic = payload.public === 'public'
    }
    delete payload.category
    delete payload.public
    if (payload.steps && payload.steps.length) {
      payload.steps = payload.steps.map((stepObject) => {
        // remove and restructure keys to match with API needs
        const { order, errors, displayName, title, ...rest } = stepObject
        return Object.assign({}, rest, { title: title.name })
      })
    }

    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateProcedure($input: UpdateProcedureInput!) {
            updateProcedure(input: $input) {
              procedure {
                name
                id
                code
                isPublic
                description
                language
                steps {
                  ...ProcedureStepDetails
                }
                checklist
                createdAt
                updateAt
                category {
                  id
                  name
                }
                author {
                  name
                }
                departments {
                  name
                  institution {
                    name
                  }
                }
              }
              status
            }
          }
          ${stepFragment.step}
        `,
        variables: { input: payload }
      })
      store.commit(
        MutationTypes.SET_ACTIVE_PROCEDURE,
        response.data.updateProcedure.procedure
      )
      if (response.data.updateProcedure.status) {
        toast.info('Procedure updated')
      }
      return response.data.updateProcedure
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.ADD_PROCEDURE_STEP](
    store: ActionContext<State, any>,
    payload
  ) {
    const activeProcedure = store.getters.activeProcedure
    if (activeProcedure.id !== payload.procedureId) {
      throw new Error('Procedure id mismatch')
    }
    const procedureData: UpdateProcedureInput = {
      id: payload.procedureId,
      steps: activeProcedure.steps.concat(payload.step)
    }
    const result = await store.dispatch(
      ActionTypes.UPDATE_PROCEDURE,
      procedureData
    )
    if (result.status) {
      toast.info(`Added new step for procedure ${result.procedure.name}`)
    }
    return result
  },

  async [ActionTypes.UPDATE_PROCEDURE_STEP](
    store: ActionContext<State, any>,
    payload
  ) {
    const activeProcedure = store.getters.activeProcedure
    if (activeProcedure.id !== payload.procedureId) {
      throw new Error('Procedure id mismatch')
    }

    // Filter out steps other than one under update, so we can merge updated cleanly back with others
    const steps = activeProcedure.steps.filter((step) => {
      return step.id !== payload.step.id
    })
    const procedureData: UpdateProcedureInput = {
      id: payload.procedureId,
      steps: steps.concat(payload.step)
    }
    const result = await store.dispatch(
      ActionTypes.UPDATE_PROCEDURE,
      procedureData
    )
    if (result.status) {
      toast.info(`Updated step for procedure ${result.procedure.name}`)
    }
    return result
  },


  async [ActionTypes.GET_INSTITUTIONS](
    store: ActionContext<State, any>,
    payload
  ) {
    if (!store.getters.activeInstitutionId) {
      await store.dispatch(ActionTypes.GET_CURRENT_USER)
    }

    const input: GetInstitutionsInput = {
      id: payload?.institutionId || [],
      pager: payload?.pager,
      search: payload?.search
    }

    try {
      const response = await client.query({
        query: gql`
          query getInstitutions($input: GetInstitutionsInput!) {
            getInstitutions(input: $input) {
              items {
                id
                name
                street
                house
                city
                county
                country
                timezone
                language
                createdAt
                administrators {
                  id
                  name
                }
              }
              pager {
                count
                limit
                offset
              }
            }
          }
        `,
        variables: { input }
      })

      store.commit(
        MutationTypes.SET_INSTITUTIONS_LIST,
        response.data.getInstitutions.items
      )

      store.commit(
        MutationTypes.SET_INSTITUTIONS_PAGEINFO,
        response.data.getInstitutions.pager
      )
      store.commit(MutationTypes.SET_REFRESH_INSTITUTIONS, false)
      return response.data.getInstitutions
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.CREATE_INSTITUTION](
    store: ActionContext<State, any>,
    input: CreateInstitutionInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createInstitution($input: CreateInstitutionInput!) {
            createInstitution(input: $input) {
              institution {
                name
              }
              status
            }
          }
        `,
        variables: { input }
      })

      return response.data.createInstitution
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.UPDATE_INSTITUTION](
    store: ActionContext<State, any>,
    payload: UpdateInstitutionInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateInstitution($input: UpdateInstitutionInput!) {
            updateInstitution(input: $input) {
              institution {
                name
              }
              status
            }
          }
        `,
        variables: { input: payload }
      })
      return response.data.updateInstitution
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.GET_STUDIES](store: ActionContext<State, any>, payload) {
    const input: GetStudiesInput = {
      institutionId: store.getters.activeInstitutionId,
      pager: payload.pager,
      search: payload.search
    }
    if (payload.id) {
      input.id = [payload.id]
    }
    if (payload.departmentIds) {
      input.departmentIds = payload.departmentIds
    }
    try {
      const response = await client.query({
        query: gql`
          query getStudies($input: GetStudiesInput!) {
            getStudies(input: $input) {
              items {
                id
                name
                description
                department {
                  id
                  name
                  institution {
                    name
                  }
                }
                author {
                  name
                }
                startsAt
                endsAt
                status
                permission
                survey {
                  id
                  oldId
                  name
                  startsAt
                  endsAt
                  status
                  position
                  results {
                    answers
                    user {
                      id
                      name
                    }
                    id
                    date
                  }
                  parentSurvey {
                    oldId
                    id
                    name
                  }
                  surveyType
                }
                createdAt
              }
              pager {
                count
                offset
                limit
              }
            }
          }
        `,
        variables: { input }
      })
      if (payload.id) {
        store.commit(
          MutationTypes.SET_ACTIVE_STUDY,
          response.data.getStudies.items?.[0]
        )
        localStorage.setItem(
          'studyResultsList',
          JSON.stringify(response.data.getStudies.items[0])
        )
      } else {
        store.commit(
          MutationTypes.SET_STUDY_LIST,
          response.data.getStudies.items
        )
        store.commit(
          MutationTypes.SET_STUDY_PAGEINFO,
          response.data.getStudies.pager
        )
        localStorage.setItem(
          'studyResultsList',
          JSON.stringify(response.data.getStudies.items)
        )
        store.state.refreshStudies = false
      }
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.CREATE_STUDY](
    store: ActionContext<State, any>,
    input: CreateStudyInput
  ) {
    input.institutionId = store.getters.activeInstitutionId

    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createStudy($input: CreateStudyInput!) {
            createStudy(input: $input) {
              study {
                name
              }
              status
              message
            }
          }
        `,
        variables: { input }
      })
      store.state.refreshStudies = true
      return response.data.createStudy
    } catch (e) {
      preventError(e);
    }
  },

  async [ActionTypes.UPDATE_STUDY](
    store: ActionContext<State, any>,
    input: UpdateStudyInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateStudy($input: UpdateStudyInput!) {
            updateStudy(input: $input) {
              study {
                id
                name
                description
                department {
                  id
                  name
                }
                author {
                  name
                }
                startsAt
                endsAt
                status
                permission
                survey {
                  id
                  oldId
                  name
                  startsAt
                  endsAt
                  status
                  position
                  parentSurvey {
                    oldId
                    id
                    name
                  }
                  surveyType
                }
                createdAt
              }
              status
              message
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_ACTIVE_STUDY,
        response.data.updateStudy.study
      )
      return response.data.updateStudy
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.DELETE_STUDY](
    store: ActionContext<State, any>,
    input: DeleteStudyInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation deleteStudy($input: DeleteStudyInput!) {
            deleteStudy(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.deleteStudy
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.GET_SURVEYS](
    store: ActionContext<State, any>,
    input: GetSurveysInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query getSurveys($input: GetSurveysInput!) {
            getSurveys(input: $input) {
              items {
                id
                oldId
                study {
                  id
                  name
                }
                parentSurvey {
                  name
                  id
                }
                surveyType
                name
                description
                author {
                  name
                }
                questions {
                  id
                  name
                  type
                  position
                  category {
                    id
                    name
                  }
                  details {
                    ... on ChoiceQuestion {
                      question
                      answers {
                        value
                      }
                    }
                    ... on ScaleQuestion {
                      question
                      optionValues {
                        value
                        meaning
                      }
                    }
                    ... on MultiChoiceQuestion {
                      question
                      answers {
                        value
                      }
                    }
                  }
                }
                surveyType
                startsAt
                endsAt
                status
                position
                childSurvey {
                  id
                  oldId
                  name
                  surveyType
                  position
                }
                overallScore
                category {
                  id
                  name
                  description
                }
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getSurveys.items
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.CREATE_SURVEY](
    store: ActionContext<State, any>,
    input: CreateStudyInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createSurvey($input: CreateSurveyInput!) {
            createSurvey(input: $input) {
              survey {
                id
                oldId
                name
                startsAt
                endsAt
                surveyType
                overallScore
              }
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.createSurvey
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.UPDATE_SURVEY](
    store: ActionContext<State, any>,
    input: UpdateSurveyInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateSurvey($input: UpdateSurveyInput!) {
            updateSurvey(input: $input) {
              survey {
                id
                name
                startsAt
                endsAt
                description
                overallScore
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.updateSurvey
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.GET_SURVEY_CHART_DATA](
    store: ActionContext<State, any>,
    input: GetSurveyChartDataInput
  ): Promise<GetSurveyChartDataResponse> {
    try {
      const response = await client.query({
        query: gql`
          query getSurveyChartData($input: GetSurveyChartDataInput!) {
            getSurveyChartData(input: $input) {
              labels
              datasets {
                avg
                categoryId
                categoryName
                sum
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getSurveyChartData
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.CREATE_QUESTION](
    store: ActionContext<State, any>,
    input: CreateQuestionInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createQuestion($input: CreateQuestionInput!) {
            createQuestion(input: $input) {
              question {
                id
                name
              }
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.createQuestion
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.UPDATE_QUESTION](
    store: ActionContext<State, any>,
    input: UpdateQuestionInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateQuestion($input: UpdateQuestionInput!) {
            updateQuestion(input: $input) {
              question {
                id
                name
                type
                category {
                  id
                  name
                }
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.updateQuestion
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.REPOSITION_SURVEY](
    store: ActionContext<State, any>,
    input: UpdateSurveyPositionInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateSurveyPosition($input: UpdateSurveyPositionInput!) {
            updateSurveyPosition(input: $input) {
              survey {
                id
                name
                position
              }
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.updateSurveyPosition
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.REPOSITION_QUESTION](
    store: ActionContext<State, any>,
    input: UpdateQuestionPositionInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateQuestionPosition(
            $input: UpdateQuestionPositionInput!
          ) {
            updateQuestionPosition(input: $input) {
              surveyQuestion {
                id
                position
              }
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.updateQuestionPosition
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.CREATE_CATEGORY](
    store: ActionContext<State, any>,
    input: CreateCategoryInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createCategory($input: CreateCategoryInput!) {
            createCategory(input: $input) {
              category {
                id
                name
              }
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.createCategory
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.UPDATE_CATEGORY](
    store: ActionContext<State, any>,
    input: UpdateCategoryInput
  ) {
    // input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateCategory($input: UpdateCategoryInput!) {
            updateCategory(input: $input) {
              name
              description
            }
          }
        `,
        variables: { input }
      })
      return response.data.updateCategory
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.DELETE_CATEGORY](
    store: ActionContext<State, any>,
    input: DeleteCategoryInput
  ) {
    // input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation deleteCategory($input: DeleteCategoryInput!) {
            deleteCategory(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.deleteCategory
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.DELETE_QUESTION_CATEGORY](
    store: ActionContext<State, any>,
    input: DeleteQuestionCategoryInput
  ) {
    // input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation deleteQuestionCategory(
            $input: DeleteQuestionCategoryInput!
          ) {
            deleteQuestionCategory(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.deleteQuestionCategory
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.GET_LEARNING_OBJECTS](
    store: ActionContext<State, any>,
    input: GetLearningObjectsInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    // input.institutionId = 'a330259a-5ee9-11eb-ae93-0242ac130002'

    try {
      const response = await client.query({
        query: gql`
          query getLearningObjects($input: GetLearningObjectsInput!) {
            getLearningObjects(input: $input) {
              items {
                id
                name
                content
                isEnabled
                createdAt
                departments {
                  id
                  name
                }
                author {
                  id
                  name
                }
              }
              pager {
                count
                offset
                limit
              }
            }
          }
        `,
        variables: { input }
      })
      if (!input.id) {
        store.commit(
          MutationTypes.SET_LEARNING_OBJECTS_LIST,
          response.data.getLearningObjects.items
        )
        store.commit(
          MutationTypes.SET_LEARNING_OBJECTS_LIST_PAGE_INFO,
          response.data.getLearningObjects.pager
        )
        store.state.refreshLearningObjects = false
      } else {
        return response.data.getLearningObjects.items[0]
      }
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },

  async [ActionTypes.CREATE_LEARNING_OBJECT](
    store: ActionContext<State, any>,
    input: CreateLearningObjectInput
  ) {
    input.institutionId = store.getters.activeInstitutionId

    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createLearningObject($input: CreateLearningObjectInput!) {
            createLearningObject(input: $input) {
              learningObject {
                id
                name
              }
              status
            }
          }
        `,
        variables: { input }
      })
      store.state.refreshLearningObjects = true
      return response.data.createLearningObject
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.UPDATE_LEARNING_OBJECT](
    store: ActionContext<State, any>,
    input: UpdateLearningObjectInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateLearningObject($input: UpdateLearningObjectInput!) {
            updateLearningObject(input: $input) {
              learningObject {
                id
                name
              }
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.updateLearningObject
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.DELETE_LEARNING_OBJECT](
    store: ActionContext<State, any>,
    input: DeleteLearningObjectInput
  ): Promise<DeleteLearningObjectResponse> {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation deleteLearningObject($input: DeleteLearningObjectInput!) {
            deleteLearningObject(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data.deleteLearningObject
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_MOST_POPULAR_PROCEDURES](
    store: ActionContext<State, any>,
    input: GetMostPopularProceduresInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetMostPopularProcedures(
            $input: GetMostPopularProceduresInput!
          ) {
            getMostPopularProcedures(input: $input) {
              mostPopularProcedures {
                description
                percent
                count
              }
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_MOST_POPULAR_PROCEDURES,
        response.data.getMostPopularProcedures.mostPopularProcedures
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_INFREQUENT_PROCEDURES](
    store: ActionContext<State, any>,
    input: GetInfrequentProceduresInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query getInfrequentProcedures($input: GetInfrequentProceduresInput!) {
            getInfrequentProcedures(input: $input) {
              infrequentsProcedures {
                description
                percent
                count
              }
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_INFREQUENT_PROCEDURES,
        response.data.getInfrequentProcedures.infrequentsProcedures
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_NURSES_EXPERIENCE_STATS](
    store: ActionContext<State, any>,
    input: GetNursesExperienceInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query getNursesExperience($input: GetNursesExperienceInput!) {
            getNursesExperience(input: $input) {
              fromFiveToTen
              fromOneToFive
              lessThanOne
              moreThanTen
              count
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_NURSES_EXPIRIENCE_RESPONSE,
        response.data.getNursesExperience
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_NURSE_AGE_EXPERIENCE_STATS](
    store: ActionContext<State, any>,
    input: GetNurseAgeExperienceStatsInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetNurseAgeExperienceStats(
            $input: GetNurseAgeExperienceStatsInput!
          ) {
            getNurseAgeExperienceStats(input: $input) {
              stats {
                maleCount
                monthOfBirth
                femaleCount
              }
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_NURSE_AGE_EXPERIENCE_STATS,
        response.data.getNurseAgeExperienceStats.stats
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_NURSE_AGE_STATS](
    store: ActionContext<State, any>,
    input: GetNurseAgeStatsInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetNurseAgeStat($input: GetNurseAgeStatsInput!) {
            getNurseAgeStats(input: $input) {
              stats {
                averageAge
                monthOfBirth
              }
            }
          }
        `,
        variables: { input }
      })

      store.commit(
        MutationTypes.SET_NURSE_AGE_STATS,
        response.data.getNurseAgeStats.stats
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_AVERAGE_SHIFT_DATA](
    store: ActionContext<State, any>,
    input: GetAverageShiftDataInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetAverageShiftData($input: GetAverageShiftDataInput!) {
            getAverageShiftData(input: $input) {
              averageShiftDuration
              averageShiftIntensity
              durationVsPreviousMonth
              intensityShiftVsPreviousWeek
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_AVERAGE_SHIFT_DATA,
        response.data.getAverageShiftData
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_AVERAGE_ACUITY_DATA](
    store: ActionContext<State, any>,
    input: GetAveragePatientAcuityDataInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetAveragePatientAcuityData(
            $input: GetAveragePatientAcuityDataInput!
          ) {
            getAveragePatientAcuityData(input: $input) {
              averageNrOfPatients
              averageNrOfPatientsVsPreviousMonth
              averageShiftAcuity
              averageShiftAcuityVsPreviousMonth
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_AVERAGE_ACUITY_DATA,
        response.data.getAveragePatientAcuityData
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_DEVICES](
    store: ActionContext<State, any>,
    input: GetDevicesInput
  ) {
    console.log('input', input)
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.query({
        query: gql`
          query GetDevices($input: GetDevicesInput!) {
            getDevices(input: $input) {
              pager {
                count
                limit
                offset
              }
              items {
                name
                lifeCycleEnd
                lifeCycleStart
                updateAt
                createdAt
                nextAuditDate
                department {
                  name
                  institution {
                    name
                  }
                }
                group {
                  name
                  lifeCycleNum
                  lifeCycleTime
                }
                id
                udi
                author {
                  name
                }
                #                procedureExecution {
                #                  date
                #                  userUuid
                #                  status
                #                }
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getDevices
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_DEVICE](
    store: ActionContext<State, any>,
    input: GetDeviceInput
  ) {
    console.log('input', input)
    // input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.query({
        query: gql`
          query GetDevice($input: GetDeviceInput!) {
            getDevice(input: $input) {
              device {
                name
                id
                udi
                lifeCycleEnd
                lifeCycleStart
                description
                createdAt
                updateAt
                department {
                  name
                  id
                }
                group {
                  id
                  name
                  lifeCycleTime
                  lifeCycleNum
                  procedure {
                    name
                    frequencyNum
                    frequencyPeriod
                  }
                }
                procedureExecution {
                  name
                  date
                  deviceUuid
                  groupProcedureUuid
                  id
                  status
                  userUuid
                }
                #                auditProcedure {
                #                  name
                #                  id
                #                  deviceProcedureUuid
                #                  frequencyNum
                #                  frequencyPeriod
                #                }
                comment {
                  comment
                  createdDate
                  deviceUuid
                  id
                }
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getDevice
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_GROUPS](
    store: ActionContext<State, any>,
    input: GetGroupsInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.query({
        query: gql`
          query GetGroups($input: GetGroupsInput!) {
            getGroups(input: $input) {
              items {
                id
                lifeCycleNum
                lifeCycleTime
                name
                description
                auditFrequency
                numberOfDevices
                lifeCycleEndingSoon
                #                createdAt
                department {
                  id
                  name
                }
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getGroups
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_GROUP](
    store: ActionContext<State, any>,
    input: GetGroupInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetGroup($input: GetGroupInput!) {
            getGroup(input: $input) {
              id
              lifeCycleNum
              lifeCycleTime
              name
              description
              auditFrequency
              numberOfDevices
              lifeCycleEndingSoon
              image
              department {
                id
                name
              }
              auditProcedure {
                id
                name
                frequencyNum
                frequencyPeriod
              }
              procedure {
                isAudit
                name
                id
                frequencyPeriod
                frequencyNum
                groupProcedureUuid
              }
              learningObject {
                name
                content
                id
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getGroup
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.CREATE_DEVICES](
    store: ActionContext<State, any>,
    input: [CreateDeviceInput]
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation createDevices($input: [CreateDeviceInput!]!) {
            createDevices(input: $input) {
              message
            }
          }
        `,
        variables: { input }
      })
      return response.data
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
      throw new Error(e)
    }
  },
  async [ActionTypes.UPDATE_DEVICE](
    store: ActionContext<State, any>,
    input: UpdateDeviceInput
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation updateDevice($input: UpdateDeviceInput!) {
            updateDevice(input: $input) {
              message
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
      throw new Error(e)
    }
  },
  async [ActionTypes.GET_DEVICE_REPORTS_TOTAL](
    store: ActionContext<State, any>,
    payload: GetDeviceReportTotalInput
  ) {
    const input = {
      ...payload,
      institutionId: store.getters.activeInstitutionId
    }
    try {
      const response = await client.query({
        query: gql`
          query GetDeviceReportTotal($input: GetDeviceReportTotalInput!) {
            getDeviceReportTotal(input: $input) {
              items {
                numberOfProcedures {
                  numberOfExecutedProcedures
                  numberOfFailedAudits
                  numberOfFinishedProcedures
                  numberOfStoppedProcedures
                  numberOfSuccessfullAudits
                }
                group {
                  name
                  id
                  department {
                    name
                    id
                  }
                }
              }
              totalRecords {
                totalNumberOfExecutedProcedures
                totalNumberOfFailedAudits
                totalNumberOfFinishedProcedures
                totalNumberOfSuccessfullAudits
                totalNumberOfStoppedProcedures
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getDeviceReportTotal
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_DEVICE_REPORT](
    store: ActionContext<State, any>,
    input: GetDeviceReportInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetDeviceReport($input: GetDeviceReportInput!) {
            getDeviceReport(input: $input) {
              records {
                numberOfSuccessfullAudits
                numberOfStoppedProcedures
                numberOfFinishedProcedures
                numberOfFailedAudits
                numberOfExecutedProcedures
              }
              items {
                deviceProcedureExecution {
                  name
                  id
                  userUuid
                  date
                  status
                }
                procedure {
                  name
                  id
                }
                device {
                  id
                  name
                }
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getDeviceReport
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.CREATE_GROUP](
    store: ActionContext<State, any>,
    input: CreateGroupInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation CreateGroup($input: CreateGroupInput!) {
            createGroup(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      return response.data
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_DEVICE_CHARTS](
    store: ActionContext<State, any>,
    input: GetDeviceChartsInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.query({
        query: gql`
          query GetDeviceCharts($input: GetDeviceChartsInput) {
            getDeviceCharts(input: $input) {
              amountAuditNormal
              amountAuditPast
              amountAuditSoon
              amountEnded
              amountEnding
              amountNormal
            }
          }
        `,
        variables: { input }
      })
      return response.data.getDeviceCharts
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_RATE_OF_FAILED_AUDITS](
    store: ActionContext<State, any>,
    input: GetRateOfFailedAuditsInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.query({
        query: gql`
          query GetRateOfFailedAudits($input: GetRateOfFailedAuditsInput) {
            getRateOfFailedAudits(input: $input) {
              failedAudits
              id
              name
            }
          }
        `,
        variables: { input }
      })
      return response.data.getRateOfFailedAudits
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_NUMBER_OF_EXECUTED_PROCEDURES_CHART_DATA](
    store: ActionContext<State, any>,
    input: GetNumberOfExecutedProceduresInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetNumberOfExecutedProcedures(
            $input: GetNumberOfExecutedProceduresInput!
          ) {
            getNumberOfExecutedProcedures(input: $input) {
              data {
                procedureId
                name
                dataset
              }
              dates
            }
          }
        `,
        variables: { input }
      })
      return response.data.getNumberOfExecutedProcedures
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_AVERAGE_PROCEDURE_TIME_CHART_DATA](
    store: ActionContext<State, any>,
    input: GetAverageTimeSpentOnProcedureInput
  ) {
    input.institutionId = store.getters.activeInstitutionId
    try {
      const response = await client.query({
        query: gql`
          query GetAverageTimeSpentOnProcedure(
            $input: GetAverageTimeSpentOnProcedureInput!
          ) {
            getAverageTimeSpentOnProcedure(input: $input) {
              data {
                dataset
                name
                procedureId
              }
              dates
            }
          }
        `,
        variables: { input }
      })
      return response.data.getAverageTimeSpentOnProcedure
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_AVERAGE_NURSE_AGE_CHART](
    store: ActionContext<State, any>,
    input: GetAverageNurseAgeChartInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetAverageNurseAgeChart($input: GetAverageNurseAgeChartInput!) {
            getAverageNurseAgeChart(input: $input) {
              data
              dates
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_NURSE_AGE_STATS,
        response.data.getAverageNurseAgeChart
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_NURSES_GENDER_CHART](
    store: ActionContext<State, any>,
    input: GetNursesGenderChartInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetNursesGenderChart($input: GetNursesGenderChartInput!) {
            getNursesGenderChart(input: $input) {
              dates
              females
              males
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_NURSE_SEX_STATS,
        response.data.getNursesGenderChart
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_FIVE_MOST_FREQUENTLY_PROCEDURES](
    store: ActionContext<State, any>,
    input: GetFiveMostFrequentlyProceduresInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetFiveMostFrequentlyProcedures(
            $input: GetFiveMostFrequentlyProceduresInput!
          ) {
            getFiveMostFrequentlyProcedures(input: $input) {
              procedure {
                id
                name
                description
              }
              numberOfExecutions
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_FIVE_MOST_FREQUENTLY_PROCEDURES,
        response.data.getFiveMostFrequentlyProcedures
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_FIVE_LEAST_FREQUENTLY_PROCEDURES](
    store: ActionContext<State, any>,
    input: GetFiveLeastFrequentlyProceduresInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetFiveLeastFrequentlyProcedures(
            $input: GetFiveLeastFrequentlyProceduresInput!
          ) {
            getFiveLeastFrequentlyProcedures(input: $input) {
              procedure {
                id
                name
                description
              }
              numberOfExecutions
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_FIVE_LEAST_FREQUENTLY_PROCEDURES,
        response.data.getFiveLeastFrequentlyProcedures
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_FIVE_MOST_ASSISTED_PROCEDURES](
    store: ActionContext<State, any>,
    input: GetFiveMostAssistedProceduresInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetFiveMostAssistedProcedures(
            $input: GetFiveMostAssistedProceduresInput!
          ) {
            getFiveMostAssistedProcedures(input: $input) {
              procedure {
                id
                name
                description
              }
              numberOfSuggestedAssistances
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_FIVE_MOST_ASSISTED_PROCEDURES,
        response.data.getFiveMostAssistedProcedures
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_SURVEY_RESULTS](
    store: ActionContext<State, any>,
    input: GetSurveyResultInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetSurveyResults($input: GetSurveyResultInput!) {
            getSurveyResults(input: $input) {
              items {
                answers
                id
              }
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_SURVEY_RESULTS,
        response.data.getSurveyResults
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_SHIFT_TIREDNESS_CHART](
    store: ActionContext<State, any>,
    input: GetShiftTirednessChartInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetShiftTirednessChart($input: GetShiftTirednessChartInput!) {
            getShiftTirednessChart(input: $input) {
              data {
                key
                value
              }
              label
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_SHIFT_TIREDNESS_CHART,
        response.data.getShiftTirednessChart
      )
      return response.data.getShiftTirednessChart.label
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_SHIFT_INTENSITY_CHART](
    store: ActionContext<State, any>,
    input: GetShiftIntensityChartInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetShiftIntensityChart($input: GetShiftIntensityChartInput!) {
            getShiftIntensityChart(input: $input) {
              data {
                key
                value
              }
              label
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_SHIFT_INTENSITY_CHART,
        response.data.getShiftIntensityChart
      )
      return response.data.getShiftIntensityChart.label
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_SHIFT_TIREDNESS_QUESTIONS](
    store: ActionContext<State, any>,
    input: GetShiftTiredenessQuestionsInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetShiftTiredenessQuestions(
            $input: GetShiftTiredenessQuestionsInput!
          ) {
            getShiftTiredenessQuestions(input: $input) {
              items {
                name
                id
                position
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getShiftTiredenessQuestions.items
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_SHIFT_INTENSITY_QUESTIONS](
    store: ActionContext<State, any>,
    input: GetShiftIntensityQuestionsInput
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetShiftIntensityQuestions(
            $input: GetShiftIntensityQuestionsInput!
          ) {
            getShiftIntensityQuestions(input: $input) {
              items {
                name
                id
                position
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getShiftIntensityQuestions.items
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_PATIENTS](store: ActionContext<State, any>, payload) {
    const { pager, search } = payload
    const input: GetPatientsInput = {
      departmentUuid: payload?.departmentUuid || [],
      institutionUuid:
        payload.institutionId || store.getters.activeInstitutionId,
      pager: pager?.value || pager,
      search
    }
    try {
      const response = await client.query({
        query: gql`
          query GetPatients($input: GetPatientsInput!) {
            getPatients(input: $input) {
              items {
                nurseUuid
                patientUuid
                departmentName
                id
                name
                createdAt
                status
                nextVisit {
                  start
                }
                visitHistory {
                  start
                  end
                  name
                }
              }
              pager {
                count
                offset
                limit
              }
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_PATIENTS_LIST,
        response.data.getPatients.items
      )
      store.commit(
        MutationTypes.SET_PATIENTS_PAGEINFO,
        response.data.getPatients.pager
      )
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.GET_PATIENT](
    store: ActionContext<State, any>,
    payload: string
  ) {
    const input = { id: payload }
    try {
      const response = await client.query({
        query: gql`
          query GetPatient($input: GetPatientInput!) {
            getPatient(input: $input) {
              name
              id
              patientUuid
              location
              street
              house
              address
              departmentUuid
              departmentName
              city
              county
              country
              primaryDiagnosis
              secondaryDiagnosis
              allergy {
                allergy
                allergyType
                reaction
                severity
              }
              user {
                name
              }
              visitHistory {
                id
                userUuid
                patientUuid
                name
                start
                end
                status
                user {
                  name
                }
              }
              medication {
                medication
                medicationType
                dosage
                frequency
                startDate
                duration
                note
              }
              phoneNumber
              email
              gender
              dateOfBirth
              nurseUuid
              assignedProcedure {
                id
                procedureUuid
                name
                startAt
                endAt
                userUuid
                category {
                  id
                  name
                }
              }
              scheduledProcedure {
                id
                procedureUuid
                name
                startAt
                endAt
                userUuid
                note
                category {
                  id
                  name
                }
              }
              procedure {
                name
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getPatient
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.SET_PAGINATION_TABLE_SHOW_SIZE](
    store: ActionContext<State, any>,
    input: number
  ) {
    try {
      store.commit(MutationTypes.SET_TABLE_SHOW_SIZE, input)
    } catch (e) {
      console.error('ERROR', e.message)
      preventError(e);
    }
  },
  async [ActionTypes.ADD_PATIENT](
    store: ActionContext<State, any>,
    payload: AddPatientInput
  ) {
    const input: AddPatientInput = {
      ...payload
    }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation Mutation($input: AddPatientInput!) {
            addPatient(input: $input) {
              id
              name
            }
          }
        `,
        variables: { input }
      })
      if (response.data.addPatient) {
        toast.success(`Patient ${response.data.addPatient.name} was added`)
        return response.data.addPatient.id
      } else if (response.errors && response.errors.length) {
        toast.warning(response.errors[0].message)
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.UPDATE_PATIENT](
    store: ActionContext<State, any>,
    payload
  ) {
    const input = {
      ...payload
    }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation UpdatePatient($input: UpdatePatientInput!) {
            updatePatient(input: $input) {
              id
            }
          }
        `,
        variables: { input }
      })
      if (response.data.updatePatient) {
        toast.success(`Patient ${response.data.updatePatient.id} was updated`)
      } else if (response.errors && response.errors.length) {
        toast.warning(response.errors[0].message)
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.UPDATE_PATIENT_PROCEDURE](
    store: ActionContext<State, any>,
    input
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation UpdateProcedureExecution(
            $input: updateProcedureExecutionInput!
          ) {
            updateProcedureExecution(input: $input) {
              id
            }
          }
        `,
        variables: { input }
      })
      if (response.data.updateProcedureExecution) {
        toast.success(
          `Procedure ${response.data.updateProcedureExecution.id} was updated`
        )
      } else if (response.errors && response.errors.length) {
        toast.warning(response.errors[0].message)
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.ADD_PATIENT_PROCEDURE](
    store: ActionContext<State, any>,
    payload: CreateProcedureExecutionInput
  ) {
    const input: CreateProcedureExecutionInput = {
      ...payload
    }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation CreateProcedureExecution(
            $input: createProcedureExecutionInput!
          ) {
            createProcedureExecution(input: $input) {
              startAt
              endAt
              id
              note
              procedureUuid
              patientUuid
              userUuid
              name
            }
          }
        `,
        variables: { input }
      })
      if (response.data.createProcedureExecution) {
        toast.success(
          `Procedure ${response.data.createProcedureExecution.id} was added`
        )
        return response.data.createProcedureExecution
      } else if (response.errors && response.errors.length) {
        toast.warning(response.errors[0].message)
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.ADD_PATIENT_VISIT](
    store: ActionContext<State, any>,
    payload: AddPatientVisitInput
  ) {
    const input: AddPatientVisitInput = {
      ...payload
    }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation Mutation($input: AddPatientVisitInput!) {
            addPatientVisit(input: $input) {
              id
              userUuid
              name
              start
              end
              status
            }
          }
        `,
        variables: { input }
      })
      if (response.data.addPatientVisit) {
        toast.success(`Visit ${response.data.addPatientVisit.id} was added`)
        return response.data.addPatientVisit
      } else if (response.errors && response.errors.length) {
        toast.warning(response.errors[0].message)
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.GET_PATIENT_CONSENT](
    store: ActionContext<State, any>,
    patientId: string
  ) {
    try {
      const response = await client.query({
        query: gql`
          query GetConsent($patientId: UUID) {
            getConsent(patientId: $patientId) {
              patientUuid
              signer
              name
              surname
              date
            }
          }
        `,
        variables: { patientId }
      })
      return response.data.getConsent
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.DELETE_PATIENT_VISIT](
    store: ActionContext<State, any>,
    input
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation DeletePatientVisit($input: DeletePatientVisitInput!) {
            deletePatientVisit(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      if (response.data.deletePatientVisit) {
        toast.success(
          `Visit was deleted ${response.data.deletePatientVisit.status}`
        )
      }
      // if (response.data.updatePatientVisit) {
      //   toast.success(`Visit ${response.data.updatePatientVisit.name} was updated`)
      // }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.UPDATE_PATIENT_VISIT](
    store: ActionContext<State, any>,
    input
  ) {
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation UpdatePatientVisit($input: UpdatePatientVisitInput!) {
            updatePatientVisit(input: $input) {
              id
            }
          }
        `,
        variables: { input }
      })
      if (response.data.updatePatientVisit) {
        toast.success(
          `Visit ${response.data.updatePatientVisit.id} was updated`
        )
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.DELETE_PATIENT_PROCEDURE](
    store: ActionContext<State, any>,
    procedureId
  ) {
    const input = { id: procedureId }
    try {
      const response = await client.mutate({
        mutation: gql`
          mutation DeleteProcedureExecution(
            $input: deleteProcedureExecutionInput!
          ) {
            deleteProcedureExecution(input: $input) {
              status
            }
          }
        `,
        variables: { input }
      })
      if (response.data.deleteProcedureExecution) {
        toast.success(
          `Procedure was deleted ${response.data.deleteProcedureExecution.status}`
        )
      }
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.GET_SHIFTS_WITH_VISITS](
    store: ActionContext<State, any>,
    payload
  ) {
    const { pager, search, dateTimeFrom, dateTimeTo, patientUuid } = payload
    const input = {
      pager: pager?.value || pager,
      search,
      dateTimeFrom,
      dateTimeTo,
      patientUuid
    }
    try {
      const response = await client.query({
        query: gql`
          query GetShiftsWithVisits($input: getShiftsWithVisitsInput!) {
            getShiftsWithVisits(input: $input) {
              items {
                id
                startTime
                endTime
                visits {
                  start
                  status
                  userUuid
                  patient {
                    patientUuid
                    name
                  }
                  checklist {
                    visit_uuid
                  }
                  end
                  id
                  user {
                    name
                  }
                }
              }
              pager {
                count
                offset
                limit
              }
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_VISITS_PAGEINFO,
        response.data.getShiftsWithVisits.pager
      )
      store.commit(
        MutationTypes.SET_SHIFT_VISITS,
        response.data.getShiftsWithVisits.items
      )
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.GET_VISIT_CHECKLIST_BY_VISIT_ID](
    store: ActionContext<State, any>,
    payload
  ) {
    const visitUuid = payload
    try {
      const response = await client.query({
        query: gql`
          query GetVisitChecklistByVisitId($visitUuid: UUID!) {
            getVisitChecklistByVisitId(visitUuid: $visitUuid) {
              instrumental_tests
              laboratory_tests
              applied_medicinal_treatment
              specialist
              anamnesis
              condition_assessment
              blood_pressure_systolic
              blood_pressure_diastolic
              consultation_plans
              complaint_data
              diagnoses
              applied_treatment
              applied_medicinal_treatment
              recommendations
              patient_status
              pulse
              weight
              height
            }
          }
        `,
        variables: { visitUuid }
      })
      return response.data.getVisitChecklistByVisitId
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.GET_SHIFTS](store: ActionContext<State, any>, input) {
    try {
      const response = await client.query({
        query: gql`
          query GetShifts($input: getShiftsInput!) {
            getShifts(input: $input) {
              items {
                id
                userUuid
                status
              }
            }
          }
        `,
        variables: { input }
      })
      return response.data.getShifts.items
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.GET_NURSES_INFO_COMBINED](
    store: ActionContext<State, any>,
    payload
  ) {
    const input = {
      institutionUuid: store.getters.activeInstitutionId,
      departmentUuid: store.getters.activeDepartmentId,
      ...payload
    }
    try {
      const response = await client.query({
        query: gql`
          query GetNursesInfoCombined($input: NursesInfoCombinedInput) {
            getNursesInfoCombined(input: $input) {
              user_uuid
              name
              arrivals {
                patient_name
                location
                uuid
                time_arrive
                time_leave
                address
              }
              locations {
                location
                time
                uuid
              }
              departmentName
              institutionName
            }
          }
        `,
        variables: { input }
      })
      store.commit(
        MutationTypes.SET_NURSE_HISTORY_LOCATION,
        response.data.getNursesInfoCombined
      )
    } catch (e) {
      preventError(e);
    }
  },
  async [ActionTypes.GET_VISITS_BY_TIME_PERIOD](
    store: ActionContext<State, any>,
    payload
  ) {
    const input = {
      institutionId: store.getters.activeInstitutionId,
      ...payload
    }
    try {
      const response = await client.query({
        query: gql`
          query GetVisitsByTimePeriod($input: GetVisitsByTimePeriodInput) {
            getVisitsByTimePeriod(input: $input) {
              id
              userUuid
              patientUuid
              name
              start
              end
              checklist {
                uuid
                visit_uuid
              }
              patient {
                name
                patientUuid
              }
            }
          }
        `,
        variables: { input }
      })
      // store.commit(
      //   MutationTypes.SET_VISITS_PAGEINFO,
      //   response.data.getShiftsWithVisits.pager
      // )
      store.commit(
        MutationTypes.SET_SHIFT_VISITS,
        response.data.getVisitsByTimePeriod
      )
      // return response.data.getVisitsByTimePeriod
    } catch (e) {
      preventError(e);
    }
  }
}
export default actions
//
